import React, { useMemo } from 'react';
import { DateTimeIcon } from '@common/bw-ui/icons';
import { Button } from '@common/bw-ui/ui/button';
import Link from 'next/link';
import { Card } from '@common/bw-ui/ui/card';
import { observer } from 'mobx-react';
import { routes } from '@portal/env/routes';
import { MyBookingData } from '@portal/types/nexudus/booking';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';
import { useApp } from '../hooks/useApp';
import { useBookings } from '../hooks/useBookings';

export const UpcomingBookingsCard: React.FC<{ booking: MyBookingData }> =
  observer(({ booking }) => {
    const { resourceImageBaseUrl } = useApp();
    const { store: bookingsStore } = useBookings();
    const { t } = useTranslation();

    const { resource, startLabel, endLabel } = useMemo(() => {
      const res = bookingsStore.resources?.find(
        (r) => r.Id === booking.ResourceId,
      );
      const start = DateTime.fromISO(booking.FromTime);
      const end = DateTime.fromISO(booking.ToTime);

      return {
        resource: res,
        startLabel: start.isValid ? start.toFormat('d LLL yyyy, HH:mm') : '',
        endLabel: end.isValid ? end.toFormat('d LLL yyyy, HH:mm') : '',
      };
    }, [booking, bookingsStore.resources]);

    const paidStatus = useMemo(() => {
      const isPaid =
        !booking.CoworkerExtraServicePrice || !!booking.CoworkerInvoicePaid;
      return {
        className: isPaid ? 'status confirmed' : 'status unpaid',
        title: isPaid
          ? t('This booking has been paid!')
          : t('This booking has not yet been paid!'),
        icon: isPaid ? (
          <div className="tw:flex tw:items-start tw:gap-1 tw:text-green-700 tw:text-xs tw:font-light tw:bg-green-100 tw:px-2 tw:py-1 tw:rounded">
            <i className="icon-check fs-18"></i>
            {t('Paid')}
          </div>
        ) : (
          <div className="tw:flex tw:items-start tw:gap-1 tw:text-red-700 tw:text-xs tw:font-light tw:bg-red-100 tw:px-2 tw:py-1 tw:rounded">
            <i className="icon-close fs-18"></i>
            {t('Unpaid')}
          </div>
        ),
      };
    }, [booking.CoworkerExtraServicePrice, booking.CoworkerInvoicePaid, t]);

    const imageUrl =
      resource?.Id && resourceImageBaseUrl
        ? `${resourceImageBaseUrl}/publicresources/getimage/${resource.Id}?h=300&w=600&cache=${resource.UpdatedOn}`
        : '';

    return (
      <Card className="tw:flex tw:flex-col tw:relative tw:w-full tw:h-full tw:border tw:border-gray-300 tw:overflow-hidden tw:group tw:cursor-pointer">
        <div className="tw:relative tw:w-full tw:h-[200px] tw:rounded-t-lg tw:overflow-hidden">
          {imageUrl ? (
            <img
              src={imageUrl}
              alt={booking.ResourceName || 'Resource Image'}
              className="tw:w-full tw:h-full tw:object-cover"
            />
          ) : (
            <div className="tw:w-full tw:h-full tw:bg-gray-200" />
          )}

          <span className="tw:absolute tw:bottom-2 tw:left-4 tw:z-10">
            <div
              className={paidStatus.className}
              data-toggle="tooltip"
              title={paidStatus.title}
            >
              {paidStatus.icon}
            </div>
          </span>
        </div>

        <div className="tw:flex tw:flex-col tw:flex-grow tw:px-4 tw:mt-4 tw:pb-4">
          <h5 className="tw:text-lg tw:font-medium tw:line-clamp-2 tw:mb-4">
            {booking.ResourceName}
          </h5>

          <div className="tw:flex tw:flex-col tw:gap-2 tw:mb-6 tw:mt-auto">
            <div className="tw:flex tw:items-start tw:gap-2">
              <DateTimeIcon className="tw:w-8 tw:h-8" />
              <div className="tw:flex tw:flex-col">
                <p className="tw:text-sm tw:font-normal tw:text-gray-700 tw:m-0">
                  {startLabel}
                </p>
                <p className="tw:text-sm tw:font-normal tw:text-gray-500 tw:m-0">
                  {endLabel}
                </p>
              </div>
            </div>
          </div>

          <Button
            asChild
            variant="primary-outline"
            size="lg"
            className="tw:w-full"
          >
            <Link href={routes.booking_by_id(booking.Id)}>View Details</Link>
          </Button>
        </div>
      </Card>
    );
  });

export const BookASpaceCard = () => (
  <Link
    href="/bookings/search"
    className="tw:flex tw:flex-col tw:relative tw:w-full tw:h-full tw:border tw:border-2 tw:border-red-100 tw:bg-red-50 hover:tw:bg-red-100 tw:rounded-lg tw:overflow-hidden tw:group tw:no-underline tw:transition-colors tw:hover:shadow-md tw:transition-shadow"
  >
    <div className="tw:flex tw:flex-col tw:items-center tw:justify-center tw:px-4 tw:py-6 tw:h-full tw:w-full">
      <div className="tw:flex tw:items-center tw:justify-center tw:rounded-full tw:bg-red-100 tw:w-16 tw:h-16 tw:mb-4">
        <DateTimeIcon className="tw:w-9 tw:h-9" strokeWidth={1.5} />
      </div>

      <h5 className="tw:text-lg tw:font-normal tw:text-gray-900 tw:mb-2">
        Book a Space
      </h5>
      <p className="tw:text-sm tw:font-normal tw:text-gray-500 tw:mb-4 tw:text-center">
        Find and book a space.
      </p>
    </div>
  </Link>
);
